// Pure helpers for the Figma kit (047 D6): initials, avatar colours, the "Showing" text,
// date-and-time lines. No React and no MUI: Jest loads this file.
import { AVATAR_PAIRS } from "./figmaTokens";
import { MONTHS, parts } from "./queuePresentation";
import { toDate } from "./ticketPresentation";

// "Omar Tarek" → "OT", "maryam" → "M", nothing → "?". Array.from keeps a character that
// takes two UTF-16 units whole.
export const initialsOf = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = (word) => Array.from(word)[0].toUpperCase();
  return words.length === 1 ? first(words[0]) : first(words[0]) + first(words[words.length - 1]);
};

// One person, one colour: a string hash of the key (the uid where there is one) picks a
// Figma avatar pair.
export const avatarPairFor = (key) => {
  const text = String(key || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_PAIRS[hash % AVATAR_PAIRS.length];
};

// "Showing 1–25": a cursor page has no total (047 D16, 044 D19). "" for an empty page.
export const showingLabel = (page, count, pageSize = 25) =>
  count > 0 ? `Showing ${page * pageSize + 1}–${page * pageSize + count}` : "";

// "Showing 1–3 of 3": a list read in full.
export const allShowingLabel = (total) => (total > 0 ? `Showing 1–${total} of ${total}` : "");

// "12,458": a count as the dashboard prints it.
export const countText = (n) => Number(n).toLocaleString("en-GB");

// "Showing 1–25 of 1,234": a cursor page whose list has a count (the Students list, 047 D33).
// Without a count it is showingLabel's "Showing 1–25".
export const showingOfLabel = (page, count, pageSize, total) => {
  const range = showingLabel(page, count, pageSize);
  return range && typeof total === "number" ? `${range} of ${countText(total)}` : range;
};

// The Tickets tab reads at most `cap` tickets (044 D35); at the cap it says "50+".
export const ticketsShowingLabel = (count, cap = 50) =>
  count > 0 ? `Showing ${count}${count >= cap ? "+" : ""} ${count === 1 ? "ticket" : "tickets"} · newest first` : "";

// A table's date cell: { date: "8 Sep 2026", time: "18:30" } ("Tue 8 Sep 2026" with
// `weekday`) in the zone given, or null without a date. 24-hour, like every time on the
// dashboard; `parts` falls back to the reader's zone for a zone it does not know.
export const dateTimeLines = (value, { timeZone, weekday = false } = {}) => {
  const date = toDate(value);
  if (!date) return null;
  const p = parts(date, {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  });
  const day = `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}`;
  return { date: weekday ? `${p.weekday} ${day}` : day, time: `${p.hour}:${p.minute}` };
};

// An SVG path for a slice of a ring (the donut and the gauge, 047 step 4): centre (cx, cy),
// radii r0 < r1, from `from` to `to` in turns clockwise from 12 o'clock (0.25 is 3 o'clock).
// A slice of a whole turn or more stops just short of it, since one arc cannot close a circle.
const ringPoint = (cx, cy, r, turn) => {
  const angle = (turn - 0.25) * 2 * Math.PI;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
};
const svgNumber = (n) => String(Number(n.toFixed(3)) || 0);

export const ringSlicePath = (cx, cy, r0, r1, from, to) => {
  const end = from + Math.min(Math.max(to - from, 0), 0.9999);
  const large = end - from > 0.5 ? 1 : 0;
  const [x1, y1] = ringPoint(cx, cy, r1, from);
  const [x2, y2] = ringPoint(cx, cy, r1, end);
  const [x3, y3] = ringPoint(cx, cy, r0, end);
  const [x4, y4] = ringPoint(cx, cy, r0, from);
  return [
    `M${svgNumber(x1)} ${svgNumber(y1)}`,
    `A${r1} ${r1} 0 ${large} 1 ${svgNumber(x2)} ${svgNumber(y2)}`,
    `L${svgNumber(x3)} ${svgNumber(y3)}`,
    `A${r0} ${r0} 0 ${large} 0 ${svgNumber(x4)} ${svgNumber(y4)}Z`,
  ].join("");
};

// A line chart's two paths in a (points − 1) × 100 box, y downwards (Reports' Revenue chart and
// trend lines, 044 D85): the line through the points and the area under it. `top` is the value
// on the top edge; 0 and below sit on the bottom edge.
export const linePaths = (points, top) => {
  const max = top > 0 ? top : 1;
  const xy = points.map((v, i) => [i, 100 - (Math.max(0, Number(v) || 0) / max) * 100]);
  const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${svgNumber(x)} ${svgNumber(y)}`).join(" ");
  return { line, area: xy.length ? `${line} L${xy.length - 1} 100 L0 100Z` : "" };
};
